import axios from 'axios';
import * as types from './ActionTypes';
import { API_URL } from 'configs';
import {
  F,
  getAppraisalSetupSectionPivotRows,
  employeePafsHttpPaths,
  legacyEmployeePafsHttpPaths,
  legacyHttpPaths,
  SECTION_CATEGORIES_PAYLOAD_KEY,
} from '../constants/legacyApiKeys';
import { normalizeMyPafRecord } from '../utils/myPafHelpers';

const regexEscape = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const stripLegacyListSelect = ["kp", "is", "Value"].join("");
const stripLegacyWeightSelect = ["kp", "i", "Weightages"].join("");

const loader = label => ({ type: types.SHOW_LOADER, common_loader_label: label });
const hideLoader = () => ({ type: types.HIDE_LOADER });

const snackbar = (msg, type = 'general_error') => ({
  type: types.SHOW_SNACKBAR,
  snackbar_notification: msg,
  snackbar_notification_type: type,
});

const handleError = (error, dispatch, validationType) => {
  const res = error?.response;
  // Handle validation errors (422) and type errors (400)
  if (res?.status === 422 || res?.status === 400) {
    if (res.data?.errors) {
      // Backend validation errors
      dispatch({ type: validationType, validation_error: res.data.errors });
    } else if (res.data?.error) {
      // Backend error message
      dispatch({ type: validationType, validation_error: { _general: [res.data.error] } });
      dispatch(snackbar(res.data.error, 'general_error'));
    } else if (res.data?.message) {
      // Alternative error message format
      dispatch({ type: validationType, validation_error: { _general: [res.data.message] } });
      dispatch(snackbar(res.data.message, 'general_error'));
    } else {
      dispatch(snackbar('Validation error occurred', 'general_error'));
    }
  } else if (res?.status === 403) {
    const errorMessage =
      res.data?.message ||
      res.data?.error ||
      'You are not authorized to perform this action.';
    dispatch(snackbar(errorMessage, 'general_error'));
  } else {
    // Handle other errors (500, 401, etc.)
    const errorMessage = res?.data?.error || res?.data?.message || `${res?.status || 'Unknown'} ${res?.statusText || 'Error'}`;
    dispatch(snackbar(errorMessage, 'general_error'));
  }
};


const fetchSuccess = (type, listKey) => data => ({
  type,
  [listKey]: data
});

const getSuccess = (type) => (data, action) => ({
  type,
  record: data,
  actionType: action
});

const unwrapAppraisalSetupRecord = (payload) => payload?.data ?? payload;

const toRelationIdArray = (relation) => {
  if (!relation) return [];
  const list = Array.isArray(relation) ? relation : Object.values(relation);
  return list
    .filter((item) => item && item.id)
    .map((item) => String(item.id));
};

/** Build PUT body from a loaded setup record (required when setting is_published to 0). */
const buildAppraisalSetupUpdatePayloadFromRecord = (record, { is_published, object_viewed_id }) => {
  const pivotRows = getAppraisalSetupSectionPivotRows(record);

  const toFiniteWeight = (w) => {
    if (w === null || w === undefined || w === '') return NaN;
    const n = Number(w);
    return Number.isFinite(n) ? n : NaN;
  };

  const normalizedSetupSections = pivotRows
    .map((item) => {
      if (!item) return null;
      const section_id = String(
        item.section_id ?? item.section?.id ?? item[F.fkId] ?? ''
      ).trim();
      if (!section_id) return null;
      const weightage = toFiniteWeight(item.weightage ?? item.pivot?.weightage);
      if (!Number.isFinite(weightage)) return null;
      const row = { section_id, weightage };
      if (item.id) row.id = String(item.id);
      return row;
    })
    .filter(Boolean);

  const sectionIds = normalizedSetupSections.map((s) => s.section_id);

  return {
    id: String(record.id),
    title: record.title,
    description: record.description ?? '',
    office_id:
      record.office_id !== undefined && record.office_id !== null && record.office_id !== ''
        ? String(record.office_id)
        : null,
    campaigns: toRelationIdArray(record.campaigns),
    designations: toRelationIdArray(record.designations),
    sections: sectionIds,
    [F.listAlternatePlural]: sectionIds,
    [F.setupPivotRows]: normalizedSetupSections.map(({ id, section_id, weightage }) => {
      const row = { [F.fkId]: section_id, weightage };
      if (id) row.id = id;
      return row;
    }),
    setup_sections: normalizedSetupSections,
    frequency:
      typeof record.frequency === 'string'
        ? record.frequency
        : record.frequency || 'Quarterly',
    appraisal_type: record.appraisal_type || null,
    is_published: Number(is_published) === 1 ? 1 : 0,
    object_viewed_id: String(object_viewed_id || ''),
  };
};

/** Bumped on reset so in-flight getAppraisalById responses cannot reopen the form. */
let appraisalSetupFetchGeneration = 0;

/** Clear sticky appraisal-setup form flags without navigating. */
export const resetAppraisalSetupFlow = () => {
  appraisalSetupFetchGeneration += 1;
  return { type: types.APPRAISALS_RESET_SETUP_FLOW };
};

/**
 * @deprecated Prefer resetAppraisalSetupFlow() + explicit navigate().
 * Kept as an alias so existing callers still clear form state safely.
 */
export const redirectToAppraisalList = () => {
  appraisalSetupFetchGeneration += 1;
  return { type: types.APPRAISALS_RESET_SETUP_FLOW };
};

export const clearAppraisalListRedirect = () => ({ type: types.APPRAISALS_CLEAR_LIST_REDIRECT });

// Reset flags related to Appraisal Progress forms/views so navigation works correctly
export const resetAppraisalProgressFlags = () => ({
  type: types.APPRAISAL_PROGRESS_RESET_FLAGS,
});

export const resetAppraisalProgressRecord = () => ({
  type: types.APPRAISAL_PROGRESS_RESET_RECORD,
});

export const toggleAppraisalSetupCategoryWindow = (showCategorySetupWindow) => ({ type: types.APPRASIAL_SHOW_CATEGORY_SETUP_WINDOW, showCategorySetupWindow: showCategorySetupWindow });

export const hideAppraisalsValidationError = (feild_key) => ({
  type: types.HIDE_APPRAISALS_FEILD_VALIDATION_ERROR,
  feild_key: feild_key
});

// ------- Appraisals --------
export const fetchAppraisals = (object_viewed_id = null) => (dispatch, getState) => {
  dispatch({ type: types.APPRAISALS_REQUEST });
  dispatch(loader());

  // Get object_viewed_id from Redux state if not provided
  const state = getState();
  const finalObjectViewedId = object_viewed_id || state?.session?.current_object_viewed_id || '';

  axios.get(`${API_URL}appraisals/appraisal-setups`, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${localStorage.getItem("token")}`
    },
    params: {
      object_viewed_id: finalObjectViewedId
    }
  })
    .then(res => {
      dispatch(hideLoader());
      dispatch(fetchSuccess(types.APPRAISALS_SUCCESS, 'appraisalsList')(res.data));
    })
    .catch(err => {
      dispatch(hideLoader());
      handleError(err, dispatch, types.APPRAISALS_VALIDATION_ERROR);
    });
};

export const getAppraisalById = (id, actionType) => dispatch => {
  const fetchGeneration = appraisalSetupFetchGeneration;
  dispatch({ type: types.APPRAISALS_REQUEST });
  dispatch(loader());

  return axios.get(`${API_URL}appraisals/appraisal-setups/${id}`, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${localStorage.getItem("token")}`
    }
  })
    .then(res => {
      dispatch(hideLoader());
      // Ignore stale responses after Cancel / sidebar left the setup flow
      if (fetchGeneration !== appraisalSetupFetchGeneration) {
        return null;
      }
      dispatch(getSuccess(types.APPRAISALS_GET_SUCCESS)(res.data, actionType));
      return res.data;
    })
    .catch(err => {
      dispatch(hideLoader());
      if (fetchGeneration !== appraisalSetupFetchGeneration) {
        return null;
      }
      handleError(err, dispatch, types.APPRAISALS_VALIDATION_ERROR);
      throw err;
    });
};

export const createOrUpdateAppraisal = (data, method = 'POST') => (dispatch, getState) => {
  dispatch({ type: types.APPRAISALS_REQUEST });
  dispatch(loader());

  // Get object_viewed_id from Redux state (automatically set by Main.js based on current route)
  const state = getState();
  const currentObjectViewedId = state?.session?.current_object_viewed_id;
  
  // Use data.object_viewed_id if provided, otherwise use current_object_viewed_id from state
  // The Axios interceptor will also add it automatically, but we set it here for clarity
  const object_viewed_id = (data.object_viewed_id && data.object_viewed_id !== null && data.object_viewed_id !== 'null' && data.object_viewed_id !== 'undefined' && data.object_viewed_id !== '')
    ? String(data.object_viewed_id) 
    : (currentObjectViewedId && currentObjectViewedId !== null && currentObjectViewedId !== 'null' && currentObjectViewedId !== 'undefined' && currentObjectViewedId !== '')
      ? String(currentObjectViewedId)
      : '';
  
  // Log warning if object_viewed_id is empty (but still proceed with request - interceptor will try to add it)
  if (!object_viewed_id || object_viewed_id === '') {
    console.warn('object_viewed_id is empty. Axios interceptor will attempt to add it from current route. Session state:', {
      current_object_viewed_id: currentObjectViewedId,
      data_object_viewed_id: data.object_viewed_id
    });
  }

  // Strip UI dropdown helper fields before posting
  const {
    campaignsValue,
    designationsValue,
    sectionsValue,
    sectionWeightages,
    sectionPivotRowIds,
    officeValue,
    appraisal_typeValue,
    frequencyValue,
    [stripLegacyListSelect]: omitListSelect,
    [stripLegacyWeightSelect]: omitWeightsSelect,
    ...dataToSend
  } = data;

  const sectionIds = Array.isArray(data.sections ?? data[F.listAlternatePlural])
    ? (data.sections ?? data[F.listAlternatePlural])
      .map(id => String(id))
      .filter(id => id && id !== 'undefined' && id !== 'null')
    : [];

  const pivotSource = Array.isArray(data[F.setupPivotRows]) ? data[F.setupPivotRows] : [];

  const toFiniteWeight = (w) => {
    if (w === null || w === undefined || w === '') return NaN;
    const n = Number(w);
    return Number.isFinite(n) ? n : NaN;
  };

  const normalizedSetupSections = pivotSource
    .map((item) => {
      if (!item) return null;
      const section_id = String(item.section_id ?? item[F.fkId] ?? '').trim();
      if (!section_id) return null;
      const weightage = toFiniteWeight(item.weightage);
      if (!Number.isFinite(weightage)) return null;
      const row = { section_id, weightage };
      if (item.id) row.id = String(item.id);
      return row;
    })
    .filter(Boolean);

  const transformedData = {
    ...dataToSend,
    campaigns: Array.isArray(data.campaigns) 
      ? data.campaigns.map(id => String(id)).filter(id => id && id !== 'undefined' && id !== 'null')
      : [],
    designations: Array.isArray(data.designations)
      ? data.designations.map(id => String(id)).filter(id => id && id !== 'undefined' && id !== 'null')
      : [],
    [F.listAlternatePlural]: sectionIds,
    sections: sectionIds,
    /** Legacy key (kpi_id) — kept for older APIs */
    [F.setupPivotRows]: normalizedSetupSections.map(({ id, section_id, weightage }) => {
      const row = { [F.fkId]: section_id, weightage };
      if (id) row.id = id;
      return row;
    }),
    /** Laravel appraisals-appraisal-setups resource expects setup_sections */
    setup_sections: normalizedSetupSections,
    // Ensure frequency is a string (not an array)
    frequency: typeof data.frequency === 'string' 
      ? data.frequency 
      : (Array.isArray(data.frequency) && data.frequency.length > 0 && data.frequency[0]?.value)
        ? data.frequency[0].value
        : data.frequency || 'Quarterly',
    // Ensure object_viewed_id is always included as a string (required field)
    object_viewed_id: object_viewed_id,
    office_id:
      data.office_id !== undefined && data.office_id !== null && data.office_id !== ""
        ? String(data.office_id)
        : null,
    // Ensure id is a string if present (for PUT requests)
    ...(data.id && { id: String(data.id) })
  };

  const url = method === 'POST'
    ? `${API_URL}appraisals/appraisal-setups`
    : `${API_URL}appraisals/appraisal-setups/${transformedData.id}`;

  return axios({
    url,
    method,
    data: transformedData,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${localStorage.getItem("token")}`
    }
  })
    .then((response) => {
      dispatch(hideLoader());
      const successMessage = method === 'POST' 
        ? 'Appraisal setup created successfully' 
        : 'Appraisal setup updated successfully';
      dispatch(snackbar(successMessage, 'success'));
      
      // Get the appraisal ID from response (for POST) or from transformedData (for PUT)
      const responseData = response.data?.data || response.data;
      const appraisalId = method === 'POST' 
        ? (responseData?.id || transformedData.id)
        : transformedData.id;
      
      if (appraisalId) {
        // Fetch the full appraisal record (including pivot rows) for Redux
        return dispatch(getAppraisalById(appraisalId, 'update'))
          .then(() => {
            // Show category setup window for both POST and PUT
            dispatch(toggleAppraisalSetupCategoryWindow(true));
            return responseData;
          })
          .catch((err) => {
            console.error('Error fetching appraisal record:', err);
            // Fallback: Store the response data directly if available
            if (responseData) {
              dispatch(getSuccess(types.APPRAISALS_GET_SUCCESS)(responseData, 'update'));
            }
            // Still show category setup window even if fetch fails
            dispatch(toggleAppraisalSetupCategoryWindow(true));
            return responseData;
          });
      }

      // Fallback: if no ID, store response data if available and show category setup window
      if (responseData) {
        dispatch(getSuccess(types.APPRAISALS_GET_SUCCESS)(responseData, 'update'));
      }
      dispatch(toggleAppraisalSetupCategoryWindow(true));
      return responseData;
    })
    .catch(err => {
      dispatch(hideLoader());
      handleError(err, dispatch, types.APPRAISALS_VALIDATION_ERROR);
    });
};

export const deleteAppraisal = (id, object_viewed_id = null) => (dispatch, getState) => {
  dispatch({ type: types.APPRAISALS_REQUEST });
  dispatch(loader());

  // Get object_viewed_id from Redux state if not provided
  const state = getState();
  const finalObjectViewedId = object_viewed_id || state?.session?.current_object_viewed_id || '';

  axios.delete(`${API_URL}appraisals/appraisal-setups/${id}`, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${localStorage.getItem("token")}`
    },
    params: {
      object_viewed_id: finalObjectViewedId
    }
  })
    .then(() => {
      dispatch(hideLoader());
      dispatch(snackbar('Appraisal deleted', 'success'));
    })
    .catch(err => {
      dispatch(hideLoader());
      handleError(err, dispatch, types.APPRAISALS_VALIDATION_ERROR);
    });
};

// ------- Appraisal Progress CRUD --------
export const fetchAppraisalProgress = (object_viewed_id = null, filters = null) => (dispatch, getState) => {
  dispatch({ type: types.APPRAISAL_PROGRESS_REQUEST });
  dispatch(loader());

  const state = getState();
  const finalObjectViewedId = object_viewed_id || state?.session?.current_object_viewed_id || '';

  // Build query parameters
  const params = {
    object_viewed_id: finalObjectViewedId,
    _ts: Date.now(),
  };
  const normalizedFrequency = String(filters?.frequency || "").toLowerCase();
  const listSource = String(filters?.listSource || "").toLowerCase();
  const useEngineList = listSource === "engine";
  const isQuarterlyRequest = !useEngineList && normalizedFrequency === "quarterly";
  const isYearlyRequest = !useEngineList && normalizedFrequency === "yearly";
  const isProbationaryRequest = ["probationary", "probational"].includes(normalizedFrequency);

  // Add filter parameters if provided
  if (filters) {
    if (filters.campaign) {
      params.campaign = filters.campaign;
    }
    if (filters.designation) {
      params.designation = filters.designation;
    }
    if (filters.department) {
      params.department = filters.department;
    }
    if (filters.month) {
      params.month = filters.month;
    }
    if (filters.pafStatus) {
      params.paf_status = filters.pafStatus;
    }
    if (filters.supervisor) {
      params.supervisor = filters.supervisor;
    }
    if (filters.frequency && (useEngineList || (!isQuarterlyRequest && !isYearlyRequest && !isProbationaryRequest))) {
      params.frequency = filters.frequency;
    }
    if (
      isQuarterlyRequest &&
      filters.search &&
      String(filters.search).trim() !== ""
    ) {
      params.search = String(filters.search).trim();
    }
  }

  const endpoint = isQuarterlyRequest
    ? `${API_URL}quarterly-appraisals`
    : isYearlyRequest
      ? `${API_URL}yearly-appraisals`
      : isProbationaryRequest
        ? `${API_URL}probationary-appraisals`
      : `${API_URL}appraisals/appraisal-progress`;

  return axios.get(endpoint, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${localStorage.getItem("token")}`
    },
    params: params
  })
    .then(res => {
      dispatch(hideLoader());

      // Monthly endpoint returns array, while quarterly returns { success, data, pagination }.
      const rawList = Array.isArray(res.data) ? res.data : (res.data?.data || []);
      const normalizedList = rawList.map((item) => {
        const employee = item.employee || {};
        const nameParts = [
          employee.firstname,
          employee.middlename,
          employee.lastname,
        ].filter(Boolean);
        const fullName = nameParts.join(" ").trim();

        const campaignSource = employee.campaign || employee.campaigns || item.campaign || item.campaigns;
        const campaignName = Array.isArray(campaignSource)
          ? campaignSource
              .map((camp) => (typeof camp === "string" ? camp : camp?.name || camp?.client_name || camp?.campaign_name || ""))
              .filter(Boolean)
              .join(", ")
          : (typeof campaignSource === "string"
              ? campaignSource
              : campaignSource?.name || campaignSource?.client_name || campaignSource?.campaign_name || "");

        const departmentSource = employee.department || item.department;
        const departmentName = typeof departmentSource === "string"
          ? departmentSource
          : departmentSource?.name || departmentSource?.department_name || "";

        const months = Array.isArray(item.months)
          ? item.months
          : Array.isArray(item.quarterly_appraisals?.months)
            ? item.quarterly_appraisals.months
            : [];
        const normalizedSingleMonth = item?.month
          ? String(item.month).trim().toLowerCase()
          : "";
        const capitalizedSingleMonth = normalizedSingleMonth
          ? `${normalizedSingleMonth.charAt(0).toUpperCase()}${normalizedSingleMonth.slice(1)}`
          : "";
        const normalizedMonthsLabel = months.length ? months.join(", ") : capitalizedSingleMonth;

        const setupName =
          item.setup_name ||
          item.appraisal_form_name ||
          item.paf_name ||
          "";
        const appraiserName =
          item.appraiser_name ||
          item.supervisor_name ||
          "";

        return {
          ...item,
          name: fullName || item.name || item.employee_name || item.employee_id || "",
          employee_name: fullName || item.employee_name || item.employee_id || "",
          department: departmentName || item.department || "",
          campaign: campaignName || item.campaign || "",
          setup_name: setupName || item.setup_name || "",
          appraisal_form_name: setupName || item.appraisal_form_name || "",
          appraiser_name: appraiserName || item.appraiser_name || "",
          supervisor_name: appraiserName || item.supervisor_name || "",
          months,
          month: normalizedMonthsLabel,
        };
      });

      dispatch({ type: types.APPRAISAL_PROGRESS_SUCCESS, appraisalProgressList: normalizedList });
    })
    .catch(err => {
      dispatch(hideLoader());
      handleError(err, dispatch, types.APPRAISAL_PROGRESS_VALIDATION_ERROR);
      throw err;
    });
};

export const getAppraisalProgressById = (id, actionType = 'update') => dispatch => {
  dispatch({ type: types.APPRAISAL_PROGRESS_REQUEST });
  dispatch(loader());

  return axios.get(`${API_URL}appraisals/appraisal-progress/${id}`, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${localStorage.getItem("token")}`
    }
  })
    .then(res => {
      dispatch(hideLoader());
      dispatch({ 
        type: types.APPRAISAL_PROGRESS_GET_SUCCESS, 
        record: res.data,
        actionType: actionType 
      });
      return res.data;
    })
    .catch(err => {
      dispatch(hideLoader());
      handleError(err, dispatch, types.APPRAISAL_PROGRESS_VALIDATION_ERROR);
      throw err;
    });
};

const appraisalProgressBearerHeaders = (contentType) => {
  const headers = {
    Accept: 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  };
  if (contentType) {
    headers['Content-Type'] = contentType;
  }
  return headers;
};

/** Normalize list/upload attachment API payloads to an array of attachment records. */
export const normalizeAppraisalProgressAttachmentsResponse = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.attachments)) return payload.attachments;
  return [];
};

export const fetchAppraisalProgressAttachments = (pafId) => (dispatch) => {
  if (!pafId) {
    return Promise.resolve([]);
  }

  return axios
    .get(`${API_URL}appraisals/appraisal-progress/${pafId}/attachments`, {
      headers: appraisalProgressBearerHeaders('application/json'),
    })
    .then((res) => normalizeAppraisalProgressAttachmentsResponse(res.data))
    .catch((err) => {
      handleError(err, dispatch, types.APPRAISAL_PROGRESS_VALIDATION_ERROR);
      throw err;
    });
};

export const uploadAppraisalProgressAttachments = (
  pafId,
  files,
  sectionContext = 'after_objective'
) => (dispatch) => {
  if (!pafId) {
    return Promise.reject(new Error('Appraisal progress id is required to upload attachments.'));
  }

  const formData = new FormData();
  (files || []).forEach((file) => {
    formData.append('attachments[]', file);
  });
  formData.append('section_context', sectionContext);

  return axios
    .post(`${API_URL}appraisals/appraisal-progress/${pafId}/attachments`, formData, {
      headers: appraisalProgressBearerHeaders(),
    })
    .then((res) => {
      const message = res.data?.message || 'Attachments uploaded successfully';
      dispatch(snackbar(message, 'success'));
      return normalizeAppraisalProgressAttachmentsResponse(res.data);
    })
    .catch((err) => {
      handleError(err, dispatch, types.APPRAISAL_PROGRESS_VALIDATION_ERROR);
      throw err;
    });
};

export const deleteAppraisalProgressAttachment = (pafId, attachmentId) => (dispatch) => {
  if (!pafId || !attachmentId) {
    return Promise.reject(new Error('Appraisal progress id and attachment id are required.'));
  }

  return axios
    .delete(
      `${API_URL}appraisals/appraisal-progress/${pafId}/attachments/${attachmentId}`,
      {
        headers: appraisalProgressBearerHeaders('application/json'),
      }
    )
    .then((res) => {
      const message = res.data?.message || 'Attachment removed';
      dispatch(snackbar(message, 'success'));
      return res.data;
    })
    .catch((err) => {
      handleError(err, dispatch, types.APPRAISAL_PROGRESS_VALIDATION_ERROR);
      throw err;
    });
};

export const createOrUpdateAppraisalProgress = (data, method = 'POST') => (dispatch, getState) => {
  dispatch({ type: types.APPRAISAL_PROGRESS_REQUEST });
  dispatch(loader());

  const state = getState();
  const currentObjectViewedId = state?.session?.current_object_viewed_id;
  
  const object_viewed_id = (data.object_viewed_id && data.object_viewed_id !== null && data.object_viewed_id !== 'null' && data.object_viewed_id !== 'undefined' && data.object_viewed_id !== '')
    ? String(data.object_viewed_id) 
    : (currentObjectViewedId && currentObjectViewedId !== null && currentObjectViewedId !== 'null' && currentObjectViewedId !== 'undefined' && currentObjectViewedId !== '')
      ? String(currentObjectViewedId)
      : '';

  // Format dates if they are Date objects
  // Handle both camelCase and snake_case field names for backward compatibility
  const transformedData = {
    ...data,
    // Format appraisal_date_time
    appraisal_date_time: data.appraisal_date_time instanceof Date 
      ? data.appraisal_date_time.toISOString() 
      : (data.appraisal_date_time || null),
    // Format sign_off_date_time (handle both camelCase and snake_case)
    sign_off_date_time: data.sign_off_date_time instanceof Date
      ? data.sign_off_date_time.toISOString()
      : (data.signOffDateTime instanceof Date
          ? data.signOffDateTime.toISOString()
          : (data.sign_off_date_time || data.signOffDateTime || null)),
    object_viewed_id: object_viewed_id,
    // Ensure all required fields are included with proper values
    appraisers_comments: data.appraisers_comments !== undefined ? data.appraisers_comments : null,
    developmental_plan: data.developmental_plan !== undefined ? data.developmental_plan : null,
    [F.sectionDataSynonym]:
      data[F.sectionDataSynonym] !== undefined ? data[F.sectionDataSynonym] : null,
    sections_data:
      data.sections_data !== undefined
        ? data.sections_data
        : data[F.sectionDataSynonym] !== undefined
          ? data[F.sectionDataSynonym]
          : null,
    objective1: data.objective1 !== undefined ? data.objective1 : null,
    objective2: data.objective2 !== undefined ? data.objective2 : null,
    objective3: data.objective3 !== undefined ? data.objective3 : null,
    sign_off: data.sign_off !== undefined ? data.sign_off : false,
    signature_image: data.signature_image !== undefined ? data.signature_image : null,
    appraisal_types: data.appraisal_types !== undefined ? data.appraisal_types : null,
    appraisal_setup_ids: data.appraisal_setup_ids !== undefined ? data.appraisal_setup_ids : [],
    // Remove UI-only fields that shouldn't be sent
    signOffDateTime: undefined, // Remove camelCase version if present
    employeeName: undefined, // Remove UI-only field
    employeeId: undefined, // Remove UI-only field (we use employee_id)
    appraisalDateTime: undefined, // Remove camelCase version
    appraisalTypes: undefined, // Remove camelCase version (we use appraisal_setup_ids)
    appraisersComments: undefined, // Remove camelCase version
    developmentalPlan: undefined, // Remove camelCase version
    signOff: undefined, // Remove camelCase version
    signatureImage: undefined, // Remove camelCase version
    ...(data.id && { id: String(data.id) })
  };

  const url = method === 'POST'
    ? `${API_URL}appraisals/appraisal-progress`
    : `${API_URL}appraisals/appraisal-progress/${transformedData.id}`;

  // Log the request for debugging (remove in production)
  if (process.env.NODE_ENV === 'development') {
    console.log('Appraisal Progress Request:', {
      url,
      method,
      object_viewed_id: transformedData.object_viewed_id,
      data: transformedData
    });
  }

  return axios({
    url,
    method,
    data: transformedData,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${localStorage.getItem("token")}`
    }
  })
    .then((response) => {
      dispatch(hideLoader());
      const successMessage = method === 'POST' 
        ? 'Appraisal progress created successfully' 
        : 'Appraisal progress updated successfully';
      dispatch(snackbar(successMessage, 'success'));
      return response.data;
    })
    .catch(err => {
      dispatch(hideLoader());
      
      // Handle 403 Forbidden specifically
      if (err?.response?.status === 403) {
        const errorMessage = err?.response?.data?.message || 'You do not have permission to perform this action.';
        dispatch(snackbar(errorMessage, 'general_error'));
        console.error('403 Forbidden - Authorization Error:', {
          message: errorMessage,
          url: url,
          object_viewed_id: transformedData.object_viewed_id,
          response: err?.response?.data
        });
      } else {
        handleError(err, dispatch, types.APPRAISAL_PROGRESS_VALIDATION_ERROR);
      }
      throw err;
    });
};

export const deleteAppraisalProgress = (id, object_viewed_id = null) => (dispatch, getState) => {
  dispatch({ type: types.APPRAISAL_PROGRESS_REQUEST });
  dispatch(loader());

  const state = getState();
  const finalObjectViewedId = object_viewed_id || state?.session?.current_object_viewed_id || '';

  return axios.delete(`${API_URL}appraisals/appraisal-progress/${id}`, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${localStorage.getItem("token")}`
    },
    params: {
      object_viewed_id: finalObjectViewedId
    }
  })
    .then(() => {
      dispatch(hideLoader());
      dispatch(snackbar('Appraisal progress deleted successfully', 'success'));
      const state = getState();
      const object_viewed_id = state?.session?.current_object_viewed_id || state?.session?.current_page_permissions?.object_id || '';
      // Note: filters are not preserved after delete, will fetch all records
      dispatch(fetchAppraisalProgress(object_viewed_id, null));
    })
    .catch(err => {
      dispatch(hideLoader());
      handleError(err, dispatch, types.APPRAISAL_PROGRESS_VALIDATION_ERROR);
    });
};

export const sendAppraisalProgressForRTM = (id, object_viewed_id = null) => (dispatch, getState) => {
  dispatch({ type: types.APPRAISAL_PROGRESS_REQUEST });
  dispatch(loader());

  const state = getState();
  const currentObjectViewedId = state?.session?.current_object_viewed_id;
  
  // Get object_viewed_id from multiple sources with fallbacks
  const finalObjectViewedId = object_viewed_id || 
    (state?.session?.current_page_permissions && !Array.isArray(state.session.current_page_permissions) 
      ? state.session.current_page_permissions.object_id 
      : null) ||
    currentObjectViewedId || 
    '';

  // Validate that object_viewed_id is provided
  if (!finalObjectViewedId || finalObjectViewedId === '') {
    dispatch(hideLoader());
    const errorMessage = 'object_viewed_id is required to send appraisal for RTM';
    dispatch(snackbar(errorMessage, 'general_error'));
    return Promise.reject(new Error(errorMessage));
  }

  // Send object_viewed_id as query parameter (recommended method per API docs)
  return axios.put(`${API_URL}appraisals/appraisal-progress/${id}/send-for-rtm`, {}, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${localStorage.getItem("token")}`
    },
    params: {
      object_viewed_id: finalObjectViewedId
    }
  })
    .then(async (response) => {
      dispatch(hideLoader());
      const successMessage = response.data?.message || 'Appraisal progress sent for RTM successfully';
      dispatch(snackbar(successMessage, 'success'));

      // Refresh appraisal progress listing after successful send-for-rtm.
      // Await keeps execution ordered and avoids race conditions in consumers.
      await dispatch(fetchAppraisalProgress(finalObjectViewedId, null));

      return response.data;
    })
    .catch(err => {
      dispatch(hideLoader());
      
      // Handle specific error cases based on API documentation
      const res = err?.response;
      if (res?.status === 400) {
        const errorMessage = res.data?.message || 'Invalid request. Please check your data.';
        dispatch(snackbar(errorMessage, 'general_error'));
      } else if (res?.status === 401) {
        const errorMessage = res.data?.message || 'Authentication required. Please login again.';
        dispatch(snackbar(errorMessage, 'general_error'));
      } else if (res?.status === 403) {
        const errorMessage = res.data?.message || 'You do not have permission to perform this action.';
        dispatch(snackbar(errorMessage, 'general_error'));
      } else if (res?.status === 404) {
        const errorMessage = res.data?.message || 'Appraisal progress record not found.';
        dispatch(snackbar(errorMessage, 'general_error'));
      } else {
        // Use general error handler for other errors
        handleError(err, dispatch, types.APPRAISAL_PROGRESS_VALIDATION_ERROR);
      }
      throw err;
    });
};

/**
 * RTM approve appraisal progress
 */
export const rtmApproveAppraisalProgress = (id, object_viewed_id = null) => (dispatch, getState) => {
  dispatch({ type: types.APPRAISAL_PROGRESS_REQUEST });
  dispatch(loader());

  const state = getState();
  const currentObjectViewedId = state?.session?.current_object_viewed_id;

  const finalObjectViewedId = object_viewed_id ||
    (state?.session?.current_page_permissions && !Array.isArray(state.session.current_page_permissions)
      ? state.session.current_page_permissions.object_id
      : null) ||
    currentObjectViewedId ||
    '';

  if (!finalObjectViewedId || finalObjectViewedId === '') {
    dispatch(hideLoader());
    const errorMessage = 'object_viewed_id is required to approve RTM';
    dispatch(snackbar(errorMessage, 'general_error'));
    return Promise.reject(new Error(errorMessage));
  }

  return axios.put(`${API_URL}appraisals/appraisal-progress/${id}/approve`, {}, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${localStorage.getItem("token")}`
    },
    params: {
      object_viewed_id: finalObjectViewedId
    }
  })
    .then((response) => {
      dispatch(hideLoader());
      const successMessage = response.data?.message || 'Appraisal approved successfully';
      dispatch(snackbar(successMessage, 'success'));
      dispatch({ type: types.APPRAISAL_PROGRESS_APPROVE_SUCCESS, record: response.data?.data });
      return response.data;
    })
    .catch(err => {
      dispatch(hideLoader());
      handleError(err, dispatch, types.APPRAISAL_PROGRESS_VALIDATION_ERROR);
      throw err;
    });
};

/**
 * RTM reject appraisal progress
 */
export const rtmRejectAppraisalProgress = (id, object_viewed_id = null) => (dispatch, getState) => {
  dispatch({ type: types.APPRAISAL_PROGRESS_REQUEST });
  dispatch(loader());

  const state = getState();
  const currentObjectViewedId = state?.session?.current_object_viewed_id;

  const finalObjectViewedId = object_viewed_id ||
    (state?.session?.current_page_permissions && !Array.isArray(state.session.current_page_permissions)
      ? state.session.current_page_permissions.object_id
      : null) ||
    currentObjectViewedId ||
    '';

  if (!finalObjectViewedId || finalObjectViewedId === '') {
    dispatch(hideLoader());
    const errorMessage = 'object_viewed_id is required to reject RTM';
    dispatch(snackbar(errorMessage, 'general_error'));
    return Promise.reject(new Error(errorMessage));
  }

  return axios.put(`${API_URL}appraisals/appraisal-progress/${id}/reject`, {}, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${localStorage.getItem("token")}`
    },
    params: {
      object_viewed_id: finalObjectViewedId
    }
  })
    .then((response) => {
      dispatch(hideLoader());
      const successMessage = response.data?.message || 'Appraisal rejected successfully';
      dispatch(snackbar(successMessage, 'success'));
      dispatch({ type: types.APPRAISAL_PROGRESS_REJECT_SUCCESS, record: response.data?.data });
      return response.data;
    })
    .catch(err => {
      dispatch(hideLoader());
      handleError(err, dispatch, types.APPRAISAL_PROGRESS_VALIDATION_ERROR);
      throw err;
    });
};

export const createSectionCategories = (data, skipRedirect = false) => (dispatch) => {
  dispatch({ type: types.APPRAISALS_REQUEST });
  dispatch(loader());

  const normalizeCategoryRow = (c, index) => ({
    ...c,
    rating:
      c?.rating === '' || c?.rating === null || c?.rating === undefined ? null : Number(c.rating),
    weightage:
      c?.weightage === '' || c?.weightage === null || c?.weightage === undefined
        ? null
        : Number(c.weightage),
    sort_order: Number.isFinite(Number(c?.sort_order)) ? Number(c.sort_order) : index + 1,
  });

  /** New shape: [{ section_setup_id, categories }]. Legacy: { [uuid]: categories[] }. */
  const coerceCategoryBlocks = (payload) => {
    const keyed = SECTION_CATEGORIES_PAYLOAD_KEY;
    if (Array.isArray(payload[keyed])) {
      return payload[keyed].map((block) => ({
        section_setup_id: String(block?.section_setup_id ?? '').trim(),
        categories: Array.isArray(block?.categories) ? block.categories : [],
      }));
    }
    const legacyMap = payload[F.categoriesMapSynonym];
    if (legacyMap && typeof legacyMap === 'object' && !Array.isArray(legacyMap)) {
      return Object.entries(legacyMap).map(([section_setup_id, categories]) => ({
        section_setup_id: String(section_setup_id).trim(),
        categories: Array.isArray(categories) ? categories : [],
      }));
    }
    return [];
  };

  const categoryBlocks = coerceCategoryBlocks(data).map(({ section_setup_id, categories }) => ({
    section_setup_id,
    categories: categories.map((c, index) => normalizeCategoryRow(c, index)),
  }));

  const appraisalSetupId = data?.appraisal_setup_id;
  if (!appraisalSetupId) {
    dispatch(hideLoader());
    dispatch(snackbar('Appraisal setup id is required to save categories', 'general_error'));
    return Promise.reject(new Error('appraisal_setup_id is required'));
  }

  const requestBody = {
    object_viewed_id:
      data.object_viewed_id != null && data.object_viewed_id !== ''
        ? String(data.object_viewed_id)
        : '',
    appraisal_setup_id: String(appraisalSetupId),
    [SECTION_CATEGORIES_PAYLOAD_KEY]: categoryBlocks,
  };

  const url = `${API_URL}${legacyHttpPaths.appraisalsSectionCategoriesStore}`;

  return axios.post(url, requestBody, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${localStorage.getItem("token")}`
    }
  })
  .then(() => {
    dispatch(hideLoader());
    dispatch(snackbar('Appraisal saved successfully', 'success'));
    if (!skipRedirect) {
      dispatch(resetAppraisalSetupFlow());
    }
  })
  .catch(err => {
    dispatch(hideLoader());
    //handleError(err, dispatch, types.APPRAISALS_VALIDATION_ERROR);
    const res = err?.response;
    if (res?.status === 422) {
      if (res.data.errors) {  
        const backendErrors = res.data.errors;
        const formattedErrors = {};

        const legacyErrRe = new RegExp(
          `^${regexEscape(F.categoriesMapSynonym)}\\.([^\\.]+)\\.(\\d+)\\.(.+)$`
        );
        const arrayErrRe = new RegExp(
          `^${regexEscape(SECTION_CATEGORIES_PAYLOAD_KEY)}\\.(\\d+)\\.categories\\.(\\d+)\\.(.+)$`
        );

        Object.keys(backendErrors).forEach((key) => {
          let match = key.match(arrayErrRe);
          if (match) {
            const [, blockIdx, index, field] = match;
            const setupBlockId = categoryBlocks[Number(blockIdx)]?.section_setup_id;
            if (!setupBlockId) return;

            if (!formattedErrors[setupBlockId]) formattedErrors[setupBlockId] = {};
            if (!formattedErrors[setupBlockId][index]) formattedErrors[setupBlockId][index] = {};

            formattedErrors[setupBlockId][index][field] = backendErrors[key];
            return;
          }

          match = key.match(legacyErrRe);
          if (!match) return;
          const [, setupBlockId, index, field] = match;

          if (!formattedErrors[setupBlockId]) formattedErrors[setupBlockId] = {};
          if (!formattedErrors[setupBlockId][index]) formattedErrors[setupBlockId][index] = {};

          formattedErrors[setupBlockId][index][field] = backendErrors[key];
        });

        console.log("Formatted Errors:", formattedErrors);
        dispatch({
          type: types.APPRAISALS_VALIDATION_ERROR,
          validation_error: formattedErrors
        });
      }
    }
    else {
      dispatch(snackbar(res?.data?.error || `${res?.status} ${res?.statusText}`));
    }
    throw err; // Re-throw to allow promise chaining
  });
};

export const publishAppraisalSetup = (id) => dispatch => {
  dispatch({ type: types.APPRAISALS_REQUEST });
  dispatch(loader());

  return axios.post(`${API_URL}appraisals/appraisal-setups/${id}/publish`, {}, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${localStorage.getItem("token")}`
    }
  })
  .then(() => {
    dispatch(hideLoader());
    dispatch(snackbar('Appraisal published successfully', 'success'));
    dispatch(resetAppraisalSetupFlow());
  })
  .catch(err => {
    dispatch(hideLoader());
    handleError(err, dispatch, types.APPRAISALS_VALIDATION_ERROR);
  });
};

/** Toggle is_published (1 = published, 0 = draft) without leaving the setup form. */
export const updateAppraisalSetupPublishStatus = (id, isPublished, object_viewed_id = '') => (dispatch, getState) => {
  dispatch({ type: types.APPRAISALS_REQUEST });
  dispatch(loader());

  const publishValue = Number(isPublished) === 1 ? 1 : 0;
  const setupId = String(id);
  const state = getState();
  const resolvedObjectViewedId =
    object_viewed_id ||
    state?.session?.current_object_viewed_id ||
    state?.session?.current_page_permissions?.object_id ||
    '';

  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  };

  const refreshSetup = () => dispatch(getAppraisalById(setupId, 'update'));

  const onSuccess = () => {
    dispatch(hideLoader());
    dispatch(
      snackbar(
        publishValue === 1 ? 'Appraisal setup published' : 'Appraisal setup set to draft',
        'success'
      )
    );
    return refreshSetup();
  };

  const onError = (err) => {
    dispatch(hideLoader());
    handleError(err, dispatch, types.APPRAISALS_VALIDATION_ERROR);
    throw err;
  };

  if (publishValue === 1) {
    return axios
      .post(
        `${API_URL}appraisals/appraisal-setups/${setupId}/publish`,
        { object_viewed_id: String(resolvedObjectViewedId || '') },
        { headers }
      )
      .then(onSuccess)
      .catch(onError);
  }

  const putDraftFromRecord = (record) => {
    const payload = buildAppraisalSetupUpdatePayloadFromRecord(unwrapAppraisalSetupRecord(record), {
      is_published: 0,
      object_viewed_id: resolvedObjectViewedId,
    });
    return axios.put(`${API_URL}appraisals/appraisal-setups/${setupId}`, payload, { headers });
  };

  const cachedRecord = state?.appraisalState?.appraisalRecord;
  const draftRequest =
    cachedRecord && String(cachedRecord.id) === setupId
      ? putDraftFromRecord(cachedRecord)
      : dispatch(getAppraisalById(setupId, 'update')).then(putDraftFromRecord);

  return draftRequest.then(onSuccess).catch(onError);
};

// ------- Appraisal Progress Workflow Actions --------

/**
 * Submit appraisal progress (Supervisor action)
 */
export const submitAppraisalProgress = (id, object_viewed_id = null) => (dispatch, getState) => {
  dispatch({ type: types.APPRAISAL_PROGRESS_REQUEST });
  dispatch(loader());

  const state = getState();
  const finalObjectViewedId = object_viewed_id || state?.session?.current_object_viewed_id || '';

  return axios.put(`${API_URL}appraisals/appraisal-progress/${id}/submit`, {}, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${localStorage.getItem("token")}`
    },
    params: {
      object_viewed_id: finalObjectViewedId
    }
  })
    .then((response) => {
      dispatch(hideLoader());
      const successMessage = response.data?.message || 'Appraisal progress submitted successfully';
      dispatch(snackbar(successMessage, 'success'));
      dispatch({ type: types.APPRAISAL_PROGRESS_SUBMIT_SUCCESS, record: response.data?.data });
      return response.data;
    })
    .catch(err => {
      dispatch(hideLoader());
      handleError(err, dispatch, types.APPRAISAL_PROGRESS_VALIDATION_ERROR);
      throw err;
    });
};

/**
 * Operations Manager approve appraisal progress
 */
export const operationsManagerApprove = (id, comments = '', object_viewed_id = null) => (dispatch, getState) => {
  dispatch({ type: types.APPRAISAL_PROGRESS_REQUEST });
  dispatch(loader());

  const state = getState();
  const finalObjectViewedId = object_viewed_id || state?.session?.current_object_viewed_id || '';

  return axios.put(`${API_URL}appraisals/appraisal-progress/${id}/approve`, {
    comments: comments,
    object_viewed_id: finalObjectViewedId
  }, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${localStorage.getItem("token")}`
    },
    params: {
      object_viewed_id: finalObjectViewedId
    }
  })
    .then((response) => {
      dispatch(hideLoader());
      const successMessage = response.data?.message || 'Appraisal approved successfully';
      dispatch(snackbar(successMessage, 'success'));
      dispatch({ type: types.APPRAISAL_PROGRESS_APPROVE_SUCCESS, record: response.data?.data });
      return response.data;
    })
    .catch(err => {
      dispatch(hideLoader());
      handleError(err, dispatch, types.APPRAISAL_PROGRESS_VALIDATION_ERROR);
      throw err;
    });
};

/**
 * Operations Manager reject appraisal progress
 */
export const operationsManagerReject = (id, comments = '', object_viewed_id = null) => (dispatch, getState) => {
  dispatch({ type: types.APPRAISAL_PROGRESS_REQUEST });
  dispatch(loader());

  const state = getState();
  const finalObjectViewedId = object_viewed_id || state?.session?.current_object_viewed_id || '';

  return axios.put(`${API_URL}appraisals/appraisal-progress/${id}/reject`, {
    comments: comments,
    object_viewed_id: finalObjectViewedId
  }, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${localStorage.getItem("token")}`
    },
    params: {
      object_viewed_id: finalObjectViewedId
    }
  })
    .then((response) => {
      dispatch(hideLoader());
      const successMessage =
        response.data?.message || 'PAF returned to supervisor successfully.';
      dispatch(snackbar(successMessage, 'success'));
      dispatch({ type: types.APPRAISAL_PROGRESS_REJECT_SUCCESS, record: response.data?.data });
      return response.data;
    })
    .catch(err => {
      dispatch(hideLoader());
      handleError(err, dispatch, types.APPRAISAL_PROGRESS_VALIDATION_ERROR);
      throw err;
    });
};

/**
 * HR approve appraisal progress
 */
export const hrApprove = (id, comments = '', object_viewed_id = null) => (dispatch, getState) => {
  dispatch({ type: types.APPRAISAL_PROGRESS_REQUEST });
  dispatch(loader());

  const state = getState();
  const finalObjectViewedId = object_viewed_id || state?.session?.current_object_viewed_id || '';

  return axios.put(`${API_URL}appraisals/appraisal-progress/${id}/approve`, {
    comments: comments,
    object_viewed_id: finalObjectViewedId
  }, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${localStorage.getItem("token")}`
    },
    params: {
      object_viewed_id: finalObjectViewedId
    }
  })
    .then((response) => {
      dispatch(hideLoader());
      const successMessage = response.data?.message || 'Appraisal approved successfully';
      dispatch(snackbar(successMessage, 'success'));
      dispatch({ type: types.APPRAISAL_PROGRESS_APPROVE_SUCCESS, record: response.data?.data });
      return response.data;
    })
    .catch(err => {
      dispatch(hideLoader());
      handleError(err, dispatch, types.APPRAISAL_PROGRESS_VALIDATION_ERROR);
      throw err;
    });
};

/**
 * Fetch appraisal activity logs
 */
export const fetchAppraisalActivityLogs = (appraisalProgressId, page = 1, perPage = 20) => (dispatch, getState) => {
  dispatch({ type: types.APPRAISAL_ACTIVITY_LOGS_REQUEST });
  dispatch(loader());

  const state = getState();
  const object_viewed_id = state?.session?.current_object_viewed_id || 
                          (state?.session?.current_page_permissions && !Array.isArray(state.session.current_page_permissions)
                            ? state.session.current_page_permissions.object_id
                            : null) || '';

  return axios.get(`${API_URL}appraisals/appraisal-activity-logs`, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${localStorage.getItem("token")}`
    },
    params: {
      appraisal_progress_id: appraisalProgressId,
      page: page,
      per_page: perPage,
      object_viewed_id: object_viewed_id
    }
  })
    .then((response) => {
      dispatch(hideLoader());
      dispatch({
        type: types.APPRAISAL_ACTIVITY_LOGS_SUCCESS,
        logs: response.data?.data || response.data || [],
        pagination: response.data?.pagination || {},
        appraisalProgressId: appraisalProgressId
      });
      return response.data;
    })
    .catch(err => {
      dispatch(hideLoader());
      dispatch({
        type: types.APPRAISAL_ACTIVITY_LOGS_ERROR,
        error: err.response?.data?.message || 'Failed to fetch activity logs'
      });
      handleError(err, dispatch, types.APPRAISAL_PROGRESS_VALIDATION_ERROR);
      throw err;
    });
};

// ------- Employee My PAFs (own records only) --------

const employeePafAuthHeaders = () => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

let activeEmployeePafHttpPaths = employeePafsHttpPaths;
let employeePafApiUsesLegacyPaths = false;

const isEmployeePafNotFound = (error) => error?.response?.status === 404;

/** Plain helper — not a React hook (must not use `use` prefix). */
const switchToLegacyEmployeePafApi = () => {
  activeEmployeePafHttpPaths = legacyEmployeePafsHttpPaths;
  employeePafApiUsesLegacyPaths = true;
};

const mapPathToLegacy = (path) =>
  String(path)
    .replace(employeePafsHttpPaths.list, legacyEmployeePafsHttpPaths.list)
    .replace("/approve", "/sign");

const buildEmployeeApprovePayload = (payload = {}) => {
  const signature = payload.employee_signature ?? payload.signature_image ?? null;
  if (employeePafApiUsesLegacyPaths) {
    return {
      employee_signature_confirmed: true,
      signature_image: signature,
      employee_comments: payload.employee_comments ?? null,
    };
  }
  return { employee_signature: signature };
};

const buildEmployeeRejectPayload = (payload = {}) => {
  const remarks = payload.remarks ?? payload.employee_comments ?? null;
  if (employeePafApiUsesLegacyPaths) {
    return { employee_comments: remarks };
  }
  return { remarks };
};

const employeePafGet = async (path, config = {}) => {
  try {
    return await axios.get(`${API_URL}${path}`, {
      headers: employeePafAuthHeaders(),
      ...config,
    });
  } catch (error) {
    if (!isEmployeePafNotFound(error) || employeePafApiUsesLegacyPaths) {
      throw error;
    }
    switchToLegacyEmployeePafApi();
    return axios.get(`${API_URL}${mapPathToLegacy(path)}`, {
      headers: employeePafAuthHeaders(),
      ...config,
    });
  }
};

/** `getBody` may be a function so the payload is rebuilt after legacy path switch. */
const employeePafMutate = async (method, path, getBody) => {
  const resolveBody = () => (typeof getBody === "function" ? getBody() : getBody);

  try {
    return await axios({
      method,
      url: `${API_URL}${path}`,
      data: resolveBody(),
      headers: employeePafAuthHeaders(),
    });
  } catch (error) {
    if (!isEmployeePafNotFound(error) || employeePafApiUsesLegacyPaths) {
      throw error;
    }
    switchToLegacyEmployeePafApi();
    return axios({
      method,
      url: `${API_URL}${mapPathToLegacy(path)}`,
      data: resolveBody(),
      headers: employeePafAuthHeaders(),
    });
  }
};

/**
 * List PAFs for the logged-in employee (`GET /api/employee-pafs` or legacy `my-pafs`).
 */
/**
 * @param {{ status?: string }} [filters] — workflow filter (`status` query param, e.g. `sent_to_rtm`).
 */
export const fetchMyPafs = (filters = {}) => async (dispatch) => {
  dispatch({ type: types.MY_PAFS_REQUEST });
  dispatch(loader());

  const listParams = { _ts: Date.now() };
  if (filters.status) {
    listParams.status = filters.status;
  }

  try {
    let res;
    try {
      res = await axios.get(`${API_URL}${employeePafsHttpPaths.list}`, {
        headers: employeePafAuthHeaders(),
        params: listParams,
      });
      activeEmployeePafHttpPaths = employeePafsHttpPaths;
      employeePafApiUsesLegacyPaths = false;
    } catch (error) {
      if (!isEmployeePafNotFound(error)) {
        throw error;
      }
      switchToLegacyEmployeePafApi();
      res = await axios.get(`${API_URL}${legacyEmployeePafsHttpPaths.list}`, {
        headers: employeePafAuthHeaders(),
        params: listParams,
      });
    }

    const rawList = Array.isArray(res.data) ? res.data : res.data?.data || [];
    const listRows = rawList.map(normalizeMyPafRecord);

    dispatch(hideLoader());
    dispatch({ type: types.MY_PAFS_SUCCESS, myPafsList: listRows });
    return listRows;
  } catch (err) {
    dispatch(hideLoader());
    handleError(err, dispatch, types.MY_PAFS_VALIDATION_ERROR);
    dispatch({ type: types.MY_PAFS_SUCCESS, myPafsList: [] });
    return [];
  }
};

/**
 * Load one employee-owned PAF for the view/sign screen.
 */
export const getMyPafById = (id) => async (dispatch) => {
  dispatch({ type: types.MY_PAFS_REQUEST });
  dispatch(loader());

  try {
    const res = await employeePafGet(activeEmployeePafHttpPaths.byId(id));
    dispatch(hideLoader());
    const record = normalizeMyPafRecord(res.data?.data || res.data);
    dispatch({ type: types.MY_PAFS_GET_SUCCESS, record });
    return record;
  } catch (err) {
    dispatch(hideLoader());
    handleError(err, dispatch, types.MY_PAFS_VALIDATION_ERROR);
    return null;
  }
};

/**
 * Employee approves/signs their PAF.
 */
export const employeeSignMyPaf = (id, payload = {}) => async (dispatch) => {
  dispatch({ type: types.MY_PAFS_REQUEST });
  dispatch(loader());

  try {
    const response = await employeePafMutate(
      "post",
      activeEmployeePafHttpPaths.approve(id),
      () => buildEmployeeApprovePayload(payload)
    );
    dispatch(hideLoader());
    const record = normalizeMyPafRecord(response.data?.data || response.data);
    const successMessage =
      response.data?.message || "PAF approved and signed successfully.";
    dispatch(snackbar(successMessage, "success"));
    dispatch({ type: types.MY_PAFS_SIGN_SUCCESS, record });
    return record;
  } catch (err) {
    dispatch(hideLoader());
    handleError(err, dispatch, types.MY_PAFS_VALIDATION_ERROR);
    return null;
  }
};

/**
 * Employee rejects their PAF.
 */
export const employeeRejectMyPaf = (id, payload = {}) => async (dispatch) => {
  dispatch({ type: types.MY_PAFS_REQUEST });
  dispatch(loader());

  try {
    const response = await employeePafMutate(
      "post",
      activeEmployeePafHttpPaths.reject(id),
      () => buildEmployeeRejectPayload(payload)
    );
    dispatch(hideLoader());
    const record = normalizeMyPafRecord(response.data?.data || response.data);
    const successMessage = response.data?.message || "PAF rejected successfully.";
    dispatch(snackbar(successMessage, "success"));
    dispatch({ type: types.MY_PAFS_SIGN_SUCCESS, record });
    return record;
  } catch (err) {
    dispatch(hideLoader());
    handleError(err, dispatch, types.MY_PAFS_VALIDATION_ERROR);
    return null;
  }
};

export const resetMyPafRecord = () => ({ type: types.MY_PAFS_RESET_RECORD });

/**
 * Delete an employee-owned PAF record.
 */
export const deleteMyPaf = (id) => async (dispatch) => {
  dispatch({ type: types.MY_PAFS_REQUEST });
  dispatch(loader());

  try {
    const response = await employeePafMutate(
      "delete",
      activeEmployeePafHttpPaths.byId(id),
      undefined
    );

    dispatch(hideLoader());
    const successMessage = response.data?.message || "PAF deleted successfully.";
    dispatch(snackbar(successMessage, "success"));
    dispatch({ type: types.MY_PAFS_DELETE_SUCCESS, id });
    return response.data;
  } catch (err) {
    dispatch(hideLoader());
    handleError(err, dispatch, types.MY_PAFS_VALIDATION_ERROR);
    return null;
  }
};